import CookieConsent from "@hub/services/auth0/cookie-consent";
import AdobeAnalyticsScript from "@hub/services/auth0/adobe-analytics/adobe-analytics"
import { cookieLevels, STATUS } from "@hub/services/auth0/cookie-consent/lib";
import { Shell } from "@hub/ui/shell";
import type { AppProps } from "next/app";
import Head from "next/head";
import { useRouter } from "next/router";
import React, { useCallback, useEffect, useState } from "react";
import "./global.css";
import { MatrixService } from "@hub/services/challenges/matrix.service";
import { JwtGameEngineSupport } from "@hub/models/challenges/engine-support";
import { ToastContainer } from "react-toastify";
import styled from "styled-components";
import "react-toastify/dist/ReactToastify.css";
import { JwtGameAvalability } from "@hub/models/challenges/jwt-game-avalability";
import { AutoRefresh } from "@hub/utils/auto-refresh";

const StyledContainer = styled(ToastContainer)`
  // https://styled-components.com/docs/faqs#how-can-i-override-styles-with-higher-specificity
  &&&.Toastify__toast-container {
    z-index: 999999999999999999;
  }
  .Toastify__toast {
  }

  .Toastify__toast-body {
    font-size: 1.6rem;
    line-height: 2.4rem;
    color: var(--snow);
    font-weight: bold;
    font-family: var(--font-primary);
  }
  .Toastify__progress-bar {
  }

  .Toastify__toast-theme--light {
    background: linear-gradient(161.54deg, #ff44dd 0.12%, #3f59e4 100%);
  }

  .Toastify__close-button > svg {
    fill: var(--N0);
  }

  .Toastify__progress-bar--success {
    background-color: var(--seafoam);
  }

  .Toastify__toast-icon {
  }

  .Toastify__toast-icon > svg {
    fill: var(--N0);
  }
`;

declare global {
  interface Window {
    OneTrust: any;
    OnetrustActiveGroups: any;
  }
}

const GTM = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'? '&l='+ l:''; j.async=true; j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','GTM-W7FRLJ');`;

const shouldIncludeAnalytics = () =>
  typeof window === "undefined"
    ? process.env.NODE_ENV === "production"
    : !window.location.host.includes("localhost");

const CustomApp: React.FC<AppProps> = (props) => {
  const { Component, pageProps } = props;
  const [consent, setConsent] = useState<any>(undefined);
  const router = useRouter();

  const puzzleJwtGameAvailability =
    process.env.NEXT_PUBLIC_PUZZLE_JWT_GAME_AVAILABILITY;

  const handleConsentChange = useCallback(
    (e) => {
      if (
        e.data === STATUS.EXPRESSED_CONSENT &&
        window.OnetrustActiveGroups !== consent
      ) {
        setConsent(window.OnetrustActiveGroups);
      }

      if (e.data === STATUS.WAITING_FOR_CONSENT) {
        window.OneTrust.OnConsentChanged(() =>
          setConsent(window.OnetrustActiveGroups),
        );
      }
    },
    [consent],
  );

  const hasCookieLevel = useCallback(
    (level) => {
      return consent && consent.includes(level);
    },
    [consent],
  );

  useEffect(() => {
    window.addEventListener("message", handleConsentChange, false);

    return () => {
      window.removeEventListener("message", handleConsentChange);
    };
  }, [handleConsentChange]);

  useEffect(() => {
    if (hasCookieLevel(cookieLevels.NECESSARY) && shouldIncludeAnalytics()) {
      const gtmScript = document.createElement("script");
      gtmScript.innerHTML = GTM;
      document.head.prepend(gtmScript);
    }
  }, [consent, hasCookieLevel, router]);

  useEffect(() => {
    if (puzzleJwtGameAvailability === JwtGameAvalability.OFFLINE) {
      (async () => {
        await MatrixService.offlineCleanUp();
      })();

      return;
    }

    MatrixService.checkSupport();

    const engineSupportSub = MatrixService.$engineSupport.subscribe(
      async (data) => {
        if (data === JwtGameEngineSupport.SUPPORTED) {
          await MatrixService.init();

          return;
        }

        if (data === JwtGameEngineSupport.NOT_SUPPORTED) {
          MatrixService.blockGame();

          return;
        }
      },
    );

    return () => {
      if (engineSupportSub) {
        engineSupportSub.unsubscribe();
      }

      MatrixService.cleanUp();
    };
  }, []);

  return (
    <>
      <CookieConsent />
      <AdobeAnalyticsScript />
      <Head>
        <title>Auth0 Developer Resources</title>
        <meta
          name="viewport"
          content="minimum-scale=1, initial-scale=1, width=device-width"
        />
      </Head>

      <Shell>
        <StyledContainer
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="dark"
        />
        {process.env.NODE_ENV === "development" &&
        process.env.NEXT_PUBLIC_WITH_AUTO_REFRESH ? (
          <AutoRefresh>
            <Component {...pageProps} />
          </AutoRefresh>
        ) : (
          <Component {...pageProps} />
        )}
      </Shell>

      {hasCookieLevel(cookieLevels.NECESSARY) && shouldIncludeAnalytics() && (
        <noscript>
          <iframe
            src="https://www.googletagmanager.com/ns.html?id=GTM-W7FRLJ"
            height="0"
            width="0"
            style={{ display: "none", visibility: "hidden" }}
            title="gtm"
          />
        </noscript>
      )}
    </>
  );
};

export default CustomApp;
